import React from "react";
import type { Training } from "../../../../api/training/Training.typed";

const HIGHLIGHT_DURATION_MS = 2500;

type Params = {
  trainings: Training[] | undefined;
  highlightTrainingId?: number;
  highlightCustomerId?: number;
};

// Scroll to and briefly flash the deep-linked training row (prefers ID, falls back to customer).
export function useTrainingRowHighlight({
  trainings,
  highlightTrainingId,
  highlightCustomerId,
}: Params) {
  const highlightRowRef = React.useRef<HTMLDivElement | null>(null);
  const flashedForRef = React.useRef<number | null>(null);
  const [flashActive, setFlashActive] = React.useState(false);

  const matchesTarget = React.useCallback(
    (training: Training): boolean => {
      if (highlightTrainingId != null) return training.ID === highlightTrainingId;
      if (highlightCustomerId != null) return training.CustomerID === highlightCustomerId;

      return false;
    },
    [highlightTrainingId, highlightCustomerId],
  );

  React.useEffect(() => {
    const targetKey = highlightTrainingId ?? highlightCustomerId;
    if (targetKey == null || !trainings) return;
    if (flashedForRef.current === targetKey) return;
    if (!trainings.some(matchesTarget)) return;

    flashedForRef.current = targetKey;
    setFlashActive(true);
    const rafId = requestAnimationFrame(() => {
      highlightRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timeoutId = setTimeout(() => setFlashActive(false), HIGHLIGHT_DURATION_MS);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [highlightTrainingId, highlightCustomerId, trainings, matchesTarget]);

  return { highlightRowRef, matchesTarget, flashActive };
}
